import { FC, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormPasswordField from "../../../components/forms/FormPasswordField";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { UpdatePasswordPayload, useAuthValidationSchemas } from "../../../datasources/requests/auth";
import { useWriteResource } from "../../../hooks/useResource";

interface Props {
	onSuccess?: () => void;
}

const cellSize = { xs: 12, md: 4 };

/**
 * CurrentUserPassword renders the UI that allows the current user to update its password.
 *
 * @constructor
 */
const CurrentUserPassword: FC<Props> = ({ onSuccess }) => {
	const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
	const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

	const { t } = useTranslation("feature.account");
	const { UpdatePasswordSchema } = useAuthValidationSchemas();
	const { post, isLoading } = useWriteResource<UpdatePasswordPayload, undefined>(
		{ route: ApiRoutes.AuthMePassword },
		{ subjectKey: "password" }
	);

	const form = useForm<UpdatePasswordPayload>({
		defaultValues: { oldPassword: "", password: "", passwordConfirm: "" },
		resolver: zodResolver(UpdatePasswordSchema),
	});

	const password = form.watch("password");
	const handleUpdatePassword = form.handleSubmit((payload: UpdatePasswordPayload) => {
		post(payload, { onSuccess });
		setShowOldPassword(false);
		setShowNewPassword(false);
		form.reset({ oldPassword: "", password: "", passwordConfirm: "" });
	});

	useEffect(() => {
		form.trigger("passwordConfirm");
	}, [form, password]);

	return (
		<Grid container spacing={2}>
			<Grid size={cellSize}>
				<FormPasswordField
					showPassword={showOldPassword}
					isLoading={isLoading}
					form={form}
					name="oldPassword"
					label={t("old password")}
					fullWidth
					onToggleShowPassword={() => setShowOldPassword(!showOldPassword)}
				/>
			</Grid>

			<Grid size={cellSize}>
				<FormPasswordField
					showPassword={showNewPassword}
					isLoading={isLoading}
					form={form}
					name="password"
					label={t("new password")}
					fullWidth
					onToggleShowPassword={() => setShowNewPassword(!showNewPassword)}
				/>
			</Grid>

			<Grid size={cellSize}>
				<FormPasswordField
					showPassword={showNewPassword}
					isLoading={isLoading}
					form={form}
					name="passwordConfirm"
					label={t("confirm password")}
					fullWidth
				/>
			</Grid>

			<Grid size={12}>
				<Stack direction="row-reverse">
					<Button
						loading={isLoading}
						className="confirm"
						loadingPosition="start"
						onClick={handleUpdatePassword}
						disabled={!form.formState.isValid}
					>
						{t("apply", { ns: "common" })}
					</Button>
				</Stack>
			</Grid>
		</Grid>
	);
};

export default CurrentUserPassword;
